/**
 * Punch-list f11 — sponsor company detail + optional logo, shown on
 * Study Detail. `study.sponsor` is a plain string (not a foreign key —
 * see the migration's rationale), so this resolves the full sponsor
 * record by case-insensitive name match. Read-only here; editing lives
 * in the admin Sponsors console (shared catalog, not per-study).
 */
import { useQuery } from '@tanstack/react-query';
import { Building2, Link as LinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { studies } from '@/api/client';
import { useHasRole } from '@/components/RoleGate';

// The platform owns the sponsor catalogue (Harish, 1 Oct 2026).
const MANAGE_ROLES = ['platform_admin', 'application_admin'] as const;

export function SponsorDetailCard({ sponsorName }: { sponsorName: string }) {
  const canManage = useHasRole(MANAGE_ROLES);

  const q = useQuery({
    queryKey: ['study', 'sponsor-detail', sponsorName],
    queryFn: () => studies.sponsorDetail(sponsorName),
    enabled: !!sponsorName,
    retry: false,
  });

  const sponsor = q.data?.sponsor;
  const hasDetail = sponsor && (sponsor.address || sponsor.contact_name || sponsor.contact_email || sponsor.contact_phone || sponsor.website || sponsor.logo_data_uri);

  return (
    <div className="card">
      <div className="flex items-center justify-between border-b border-surface-border bg-surface-muted/50 px-4 py-3">
        <h2 className="text-sm font-semibold text-ink-primary">Sponsor</h2>
        {canManage && (
          <Link to="/admin/sponsors" className="flex items-center gap-1 text-xs text-accent-700 hover:underline">
            <LinkIcon className="h-3 w-3" /> Manage sponsors
          </Link>
        )}
      </div>
      <div className="flex items-center gap-4 p-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded border border-surface-border bg-surface-muted">
          {sponsor?.logo_data_uri ? (
            <img src={sponsor.logo_data_uri} alt="" className="h-full w-full object-contain" />
          ) : (
            <Building2 className="h-6 w-6 text-ink-muted" />
          )}
        </div>
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-medium text-ink-primary">{sponsorName}</div>
          {!q.isLoading && !hasDetail && (
            <p className="mt-0.5 text-xs text-ink-muted">
              No company detail on file yet.
              {canManage && (
                <>
                  {' '}
                  <Link to="/admin/sponsors" className="text-accent-700 hover:underline">Add it</Link>.
                </>
              )}
            </p>
          )}
          {hasDetail && (
            <dl className="mt-1 grid grid-cols-1 gap-x-4 gap-y-0.5 text-xs text-ink-muted sm:grid-cols-2">
              {sponsor.address && <div><dt className="inline font-medium text-ink-secondary">Address: </dt><dd className="inline">{sponsor.address}</dd></div>}
              {sponsor.contact_name && <div><dt className="inline font-medium text-ink-secondary">Contact: </dt><dd className="inline">{sponsor.contact_name}</dd></div>}
              {sponsor.contact_email && <div><dt className="inline font-medium text-ink-secondary">Email: </dt><dd className="inline">{sponsor.contact_email}</dd></div>}
              {sponsor.contact_phone && <div><dt className="inline font-medium text-ink-secondary">Phone: </dt><dd className="inline">{sponsor.contact_phone}</dd></div>}
              {sponsor.website && (
                <div className="sm:col-span-2">
                  <dt className="inline font-medium text-ink-secondary">Website: </dt>
                  <dd className="inline">
                    <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="text-accent-700 hover:underline">
                      {sponsor.website}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}
